import { ChangeEvent, KeyboardEvent, useCallback, useRef, useState, VFC } from 'react';

import SVG from 'assets/svgs/svg';
import { Key, OFF } from 'types/browserConstants';

interface TextInputProps {
  value: string;
  onChange: (newValue: string) => void;
  onSubmit: (message: string) => void;
  disabled?: boolean;
  placeholderText?: string;
}

const MAX_ROWS = 4;

export const TextInput: VFC<TextInputProps> = ({
  value,
  onChange,
  onSubmit,
  disabled,
  placeholderText,
}) => {
  const [rows, setRows] = useState(1);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const maybeIncrementRows = () => {
    setRows((r) => Math.min(r + 1, MAX_ROWS));
  };

  const handleSubmit = useCallback(() => {
    if (value === '') {
      return;
    }
    onSubmit(value);
    onChange('');
    setRows(1);
    inputRef.current?.focus();
  }, [onChange, onSubmit, value]);

  return (
    <div className="flex h-max items-center rounded border border-solid border-gray-500 bg-background-surface">
      <textarea
        className="bg-transparent mb-3 ml-4 mt-3 h-max w-full resize-none border-none text-white outline-none"
        disabled={disabled}
        ref={inputRef}
        placeholder={placeholderText ? placeholderText : 'Type to respond'}
        autoComplete={OFF}
        rows={rows}
        value={value}
        onKeyDown={(e: KeyboardEvent<HTMLTextAreaElement>) => {
          switch (e.key) {
            case Key.Enter:
              if (e.shiftKey) {
                maybeIncrementRows();

                return;
              }
              e.preventDefault();
              handleSubmit();
              break;
            default:
          }
        }}
        onKeyUp={(e: KeyboardEvent<HTMLTextAreaElement>) => {
          // Edge-case: With only one row visible, user types beyond
          // the width of the textarea, causing an inner scroll context.
          // Scrolling in a single row is painful so we'll increment the
          // rows attribute to make it nicer.
          if (rows === 1 && e.currentTarget.scrollHeight > 30) {
            maybeIncrementRows();
          }
        }}
        onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
          onChange(e.target.value);
          if (e.target.value === '') {
            setRows(1);
          }
        }}
      ></textarea>
      <button
        className="bg-inherit mx-2 rounded border-none p-1 focus:outline focus:outline-1 focus:outline-focus-outline"
        disabled={disabled}
        onClick={handleSubmit}
      >
        <SVG name="send" width="24px" height="24px" fill="#FFFFFF" />
      </button>
    </div>
  );
};
